import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { InlineFieldInput } from '@/components/inline-field-group'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { ActionPopover } from '@/components/action-popover'
import { LoadingButton } from '@/components/loading-button'
import { Plus, Copy, Check, KeyRound } from 'lucide-react'
import { useApiKeys, useCreateApiKey, useRevokeApiKey } from '@/api/hooks'
import { parseUTCDate } from '@/lib/utils'

function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '-'
  return parseUTCDate(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function ApiKeysPage() {
  const { data: keys, isLoading } = useApiKeys()
  const [createOpen, setCreateOpen] = useState(false)

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-bold tracking-tight">API Keys</h1>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" /> Create API Key
            </Button>
          </DialogTrigger>
          <CreateApiKeyDialog onClose={() => setCreateOpen(false)} />
        </Dialog>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Key Prefix</TableHead>
                  <TableHead>Scopes</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Last Used</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[100px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {keys?.length ? (
                  keys.map((key) => <ApiKeyRow key={key.id} apiKey={key} />)
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-muted-foreground">
                      No API keys yet. Create one to get started.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {/* Mobile cards */}
          <div className="space-y-2 md:hidden">
            {keys?.length ? (
              keys.map((key) => <ApiKeyCard key={key.id} apiKey={key} />)
            ) : (
              <div className="rounded-lg border bg-card py-4 text-center text-sm text-muted-foreground">
                No API keys yet. Create one to get started.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

function ApiKeyRow({
  apiKey,
}: {
  apiKey: {
    id: number
    key_prefix: string
    name: string
    scopes?: string | null
    created_at: string
    last_used_at?: string | null
    expires_at?: string | null
    is_active: boolean
  }
}) {
  return (
    <TableRow>
      <TableCell className="font-medium">{apiKey.name}</TableCell>
      <TableCell>
        <code className="text-sm">{apiKey.key_prefix}...</code>
      </TableCell>
      <TableCell className="text-muted-foreground">{apiKey.scopes || '-'}</TableCell>
      <TableCell className="text-muted-foreground">{formatDateTime(apiKey.created_at)}</TableCell>
      <TableCell className="text-muted-foreground">
        {formatDateTime(apiKey.last_used_at)}
      </TableCell>
      <TableCell className="text-muted-foreground">{formatDateTime(apiKey.expires_at)}</TableCell>
      <TableCell>
        <Badge variant={apiKey.is_active ? 'default' : 'secondary'}>
          {apiKey.is_active ? 'Active' : 'Revoked'}
        </Badge>
      </TableCell>
      <TableCell>
        {apiKey.is_active && (
          <RevokeApiKeyButton apiKey={apiKey} align="end" />
        )}
      </TableCell>
    </TableRow>
  )
}

function ApiKeyCard({
  apiKey,
}: {
  apiKey: {
    id: number
    key_prefix: string
    name: string
    scopes?: string | null
    created_at: string
    last_used_at?: string | null
    expires_at?: string | null
    is_active: boolean
  }
}) {
  return (
    <div className="rounded-lg border bg-card p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="break-words text-sm font-medium">{apiKey.name}</p>
          <code className="mt-0.5 block break-all text-xs text-muted-foreground">
            {apiKey.key_prefix}...
          </code>
        </div>
        <Badge variant={apiKey.is_active ? 'default' : 'secondary'} className="shrink-0">
          {apiKey.is_active ? 'Active' : 'Revoked'}
        </Badge>
      </div>
      <div className="mt-2 space-y-1 border-t pt-2 text-xs">
        {apiKey.scopes && (
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">Scopes</span>
            <span className="break-all text-right">{apiKey.scopes}</span>
          </div>
        )}
        <div className="flex justify-between gap-2">
          <span className="text-muted-foreground">Created</span>
          <span>{formatDateTime(apiKey.created_at)}</span>
        </div>
        <div className="flex justify-between gap-2">
          <span className="text-muted-foreground">Last Used</span>
          <span>{formatDateTime(apiKey.last_used_at)}</span>
        </div>
        <div className="flex justify-between gap-2">
          <span className="text-muted-foreground">Expires</span>
          <span>{formatDateTime(apiKey.expires_at)}</span>
        </div>
      </div>
      {apiKey.is_active && (
        <div className="mt-3">
          <RevokeApiKeyButton apiKey={apiKey} className="w-full" />
        </div>
      )}
    </div>
  )
}

function RevokeApiKeyButton({
  apiKey,
  className,
  align,
}: {
  apiKey: { id: number; name: string }
  className?: string
  align?: 'start' | 'end'
}) {
  const revokeMutation = useRevokeApiKey()
  return (
    <ActionPopover
      align={align}
      trigger={
        <Button variant="destructive" size="sm" className={className}>
          Revoke
        </Button>
      }
      title="Revoke API Key"
      confirmLabel="Revoke Key"
      confirmVariant="destructive"
      pending={revokeMutation.isPending}
      pendingLabel="Revoking..."
      onConfirm={(_note, close) => revokeMutation.mutate(apiKey.id, { onSuccess: close })}
    >
      <p className="text-xs text-muted-foreground">
        Are you sure you want to revoke &quot;{apiKey.name}&quot;? This action cannot be undone.
        Any applications using this key will lose access.
      </p>
    </ActionPopover>
  )
}

function CreateApiKeyDialog({ onClose }: { onClose: () => void }) {
  const createMutation = useCreateApiKey()
  const [name, setName] = useState('')
  const [scopes, setScopes] = useState('')
  const [expiresAt, setExpiresAt] = useState('')
  const [rawKey, setRawKey] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  function handleCreate() {
    createMutation.mutate(
      {
        name,
        scopes: scopes || null,
        expires_at: expiresAt || null,
      },
      {
        onSuccess: (data) => {
          setRawKey(data.raw_key)
        },
      },
    )
  }

  function handleCopy() {
    if (rawKey) {
      navigator.clipboard.writeText(rawKey)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  function handleClose() {
    setName('')
    setScopes('')
    setExpiresAt('')
    setRawKey(null)
    setCopied(false)
    onClose()
  }

  if (rawKey) {
    return (
      <DialogContent onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>API Key Created</DialogTitle>
          <DialogDescription>
            Copy your API key now. This key will not be shown again.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <code className="flex-1 rounded-md border bg-muted px-3 py-2 text-sm break-all">
              {rawKey}
            </code>
            <Button variant="outline" size="icon" onClick={handleCopy}>
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
          <p className="text-sm font-medium text-destructive">
            <KeyRound className="mr-1 inline h-4 w-4" />
            Store this key securely. You won&apos;t be able to see it again.
          </p>
        </div>
        <DialogFooter>
          <Button onClick={handleClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    )
  }

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Create API Key</DialogTitle>
        <DialogDescription>
          Create a new API key for programmatic access to the dashboard API.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="key-name">Name</Label>
          <InlineFieldInput
            id="key-name"
            placeholder="e.g. Production CLI"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="key-scopes">Scopes (optional)</Label>
          <InlineFieldInput
            id="key-scopes"
            placeholder="e.g. cli:read,cli:write"
            value={scopes}
            onChange={(e) => setScopes(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="key-expires">Expires At (optional)</Label>
          <InlineFieldInput
            id="key-expires"
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={handleClose}>
          Cancel
        </Button>
        <LoadingButton
          loading={createMutation.isPending}
          loadingText="Creating..."
          onClick={handleCreate}
          disabled={!name.trim()}
        >
          Create Key
        </LoadingButton>
      </DialogFooter>
    </DialogContent>
  )
}
